import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  HostListener,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { PatientService } from '../../../services/patient.service';
import { AiService } from '../../../services/ai.service';
import { ErrorPanelService } from '../../../services/error-panel.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  ActivePatientFieldsForAiAnalysis,
  PatientAppointment,
} from '../../../models/patient-model';
import { AIAppointmentPipelineStatus } from '../../../models/ai-model';
import { SettingsDialogComponent } from './settings-dialog/settings-dialog.component';
import { EMPTY, Subject, interval, Subscription, finalize } from 'rxjs';
import {
  catchError,
  takeUntil,
  startWith,
  switchMap,
  takeWhile,
  tap,
  take,
} from 'rxjs/operators';
import { SharedModule } from '../../../shared/shared.module';
import { ClineaseButtonComponent } from '../../../shared/clinease-button/clinease-button.component';
import { aiPatient_appointmentAnalysis_infoText } from '../../../models/descriptions/info-texts/info-texts';
import { PatientAiAnalysisService } from '../patient-ai-analysis.service';
import { AppointmentsService } from '../../../services/appointments.service';
import { DoctorService } from '../../../services/doctor.service';
import { PaymentsService } from '../../../services/payments.service';
import { ClineaseSubscriptionStatus } from '../../../models/payments-model';

@Component({
  selector: 'app-appointment-analysis',
  standalone: true,
  templateUrl: './appointment-analysis.component.html',
  styleUrls: [
    './appointment-analysis.component.scss',
    '../patient-ai-analysis.component.scss',
  ],
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    SharedModule,
    ClineaseButtonComponent,
  ],
  providers: [DatePipe],
})
export class AppointmentAnalysisComponent implements OnChanges, OnDestroy {
  @Input() patient!: ActivePatientFieldsForAiAnalysis;
  patientAppointments: PatientAppointment[] = [];
  pastAppointmentsDataSource: MatTableDataSource<PatientAppointment>;
  uploadProgress: { [key: string]: number } = {};
  isFileUploading: boolean = false;
  uploadingAppointments: Set<string> = new Set();
  uploadedAppointmentsInThisSession: Set<string> = new Set();
  currentAppointment: PatientAppointment | null = null;
  private pollingSubscription: Subscription | null = null;

  private destroy$ = new Subject<void>();
  private readonly refreshCredits$ = new Subject<void>();
  private appointmentCredits: number | null = null;
  private subscriptionRenewalDate: string | null = null;
  private subscriptionRequestId = 0;
  private pipelineWasInProgress = false;
  private destroyed = false;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  isLoading: boolean = true;

  showInfoPanel: boolean = false;
  infoTitle: string = aiPatient_appointmentAnalysis_infoText.title;
  infoText: string = aiPatient_appointmentAnalysis_infoText.text;

  displayedColumns: string[] = [
    'title',
    'date',
    'actions',
    'aiState',
    'navigateToAppointment',
    'moreActions',
  ];

  isMobile = window.innerWidth < 1024;
  readonly radius = 10; //fixed value. Reading it from the DOM makes Safari unhappy
  readonly circumference = 2 * Math.PI * this.radius;

  constructor(
    private patientService: PatientService,
    private aiService: AiService,
    private patientAiAnalysisService: PatientAiAnalysisService,
    public errorPanelService: ErrorPanelService,
    private appointmentsService: AppointmentsService,
    private doctorService: DoctorService,
    private paymentsService: PaymentsService,
    private snackBar: MatSnackBar,
    private clipboard: Clipboard,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.pastAppointmentsDataSource =
      new MatTableDataSource<PatientAppointment>([]);
    this.observeAppointmentCredits();
  }

  get creditsExhausted(): boolean {
    return (
      !this.isAdmin &&
      this.appointmentCredits !== null &&
      this.appointmentCredits <= 0
    );
  }

  get creditsExhaustedMessage(): string {
    if (this.subscriptionRenewalDate) {
      return `I crediti per l'analisi degli appuntamenti sono terminati. Si ripristineranno il ${this.subscriptionRenewalDate}.`;
    }
    return "I crediti per l'analisi degli appuntamenti sono terminati.";
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['patient'] &&
      changes['patient'].currentValue &&
      changes['patient'].currentValue.id
    ) {
      this.initializeComponent();
    }
  }

  private initializeComponent() {
    this.showPatientInterviews();
    this.refreshCredits$.next();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling();
  }

  showPatientInterviews() {
    this.isLoading = true;
    if (!this.patient || !this.patient.id) {
      console.log('Patient data not fully loaded yet');
      return;
    }

    console.log('Fetching initial patient appointments');
    this.patientService.getPastAppointments(this.patient.id).subscribe({
      next: (response: PatientAppointment[]) => {
        this.patientAppointments = response;
        this.pastAppointmentsDataSource.data = response;
        this.startPolling(); // Start polling after initial data fetch
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching initial appointments:', err);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare gli appuntamenti'
        );
      },
    });
  }

  copyAppointmentId(appointment: PatientAppointment): void {
    const copied = this.clipboard.copy(appointment.id);
    if (copied) {
      this.snackBar.open('ID appuntamento copiato', 'Chiudi', {
        duration: 3000,
      });
      return;
    }
    this.errorPanelService.showErrorSnackBar(
      "Non è stato possibile copiare l'ID appuntamento"
    );
  }

  downloadTranscription(appointment: PatientAppointment) {
    this.aiService.getTranscription(appointment.id).subscribe({
      next: (response: Blob) => {
        const blobUrl = URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `trascrizione_${appointment.title}.docx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      },
      error: (err) => {
        console.error('Errore nello scaricamento della trascrizione', err);
        this.errorPanelService.showErrorSnackBar(
          "C'è stato un errore nello scaricamento della trascrizione"
        );
      },
    });
  }

  deleteAIAppointmentOutputs(appointment: PatientAppointment) {
    this.aiService.deleteAIAppointmentOutputs(appointment.id).subscribe({
      next: (response) => {
        this.uploadedAppointmentsInThisSession.delete(appointment.id);

        var snackBarText: string;
        if (appointment.ai_pipeline_state === 'SUCCESSFULLY_EXECUTED') {
          snackBarText = 'Risultati cancellati con successo';
        } else if (appointment.ai_pipeline_state == 'READY_TO_START') {
          snackBarText = 'Registrazione cancellata con successo';
        } else {
          snackBarText = 'Operazione completata'; // NOTE: it will never happen, but I put it here to make the compiler happy
        }
        this.snackBar.open(snackBarText, 'Chiudi', { duration: 3000 });

        // Fetch updated appointment data
        this.patientService.getPastAppointments(this.patient.id).subscribe({
          next: (updatedAppointments: PatientAppointment[]) => {
            this.patientAppointments = updatedAppointments;
            this.pastAppointmentsDataSource.data = updatedAppointments;
            // Notify that an appointment was deleted
            this.patientAiAnalysisService.notifyAppointmentAIOutputsDeleted();
            this.changeDetectorRef.detectChanges();
          },
          error: (err) => {
            console.error('Error fetching updated appointments:', err);
            this.errorPanelService.showErrorSnackBar(
              'Errore nel recuperare gli appuntamenti aggiornati'
            );
          },
        });
      },
      error: (err) => {
        console.error('Errore durante la cancellazione dei risultati', err);
        this.errorPanelService.showErrorSnackBar(
          'Errore durante la cancellazione dei risultati'
        );
      },
    });
  }

  triggerFileInput(appointment: PatientAppointment): void {
    if (this.creditsExhausted) {
      return;
    }
    console.log('triggerFileInput called');
    this.currentAppointment = appointment;
    if (this.fileInput) {
      console.log('fileInput exists');
      if (this.fileInput.nativeElement) {
        console.log('fileInput.nativeElement exists');
        this.fileInput.nativeElement.click();
      } else {
        console.log('fileInput.nativeElement is null or undefined');
      }
    } else {
      console.log('fileInput is null or undefined');
    }
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file && this.currentAppointment) {
      this.handleFile(file, this.currentAppointment);
    }
    // Reset the file input so the same file can be selected again
    this.fileInput.nativeElement.value = '';
  }

  handleFile(file: File, appointment: PatientAppointment): void {
    if (this.creditsExhausted) {
      return;
    }
    this.startFileUpload(appointment.id);

    this.appointmentsService
      .getSignedUrl(appointment.id, file.type)
      .subscribe((signedUrl: string) => {
        this.aiService
          .setEventHandlersAndSendUploadRequest(signedUrl, file)
          .pipe(
            tap((progress) => {
              if (typeof progress === 'number') {
                this.setUploadProgress(appointment.id, progress);
              }
            }),
            finalize(() => {
              this.appointmentsService
                .notifyUploadComplete(appointment.id)
                .subscribe({
                  next: () => {
                    this.handleUploadSuccess(appointment.id);
                  },
                  error: (err) => {
                    console.error('Error uploading file', err);
                    this.setUploadError(appointment.id);
                  },
                });
            })
          )
          .subscribe({
            next: () => {},
            error: (err) => {
              console.error('Error uploading file', err);
              this.setUploadError(appointment.id);
            },
          });
      });
  }

  private startFileUpload(appointmentId: string): void {
    this.uploadingAppointments.add(appointmentId);
    this.uploadProgress[appointmentId] = 0;
    this.changeDetectorRef.detectChanges();
  }

  private finishFileUpload(appointmentId: string): void {
    this.uploadingAppointments.delete(appointmentId);
    this.uploadedAppointmentsInThisSession.add(appointmentId);
    this.changeDetectorRef.detectChanges();
  }

  private handleUploadSuccess(appointmentId: string): void {
    // Update local state immediately
    const appointment = this.pastAppointmentsDataSource.data.find(
      (a) => a.id === appointmentId
    );
    if (appointment) {
      appointment.ai_pipeline_state = AIAppointmentPipelineStatus.IN_PROGRESS;
      this.updateAppointmentInDataSource(appointment);
    }

    this.startPolling();
    this.finishFileUpload(appointmentId);
    this.snackBar.open('Analisi Appuntamento iniziata', 'Chiudi', {
      duration: 3000,
    });
  }

  isUploading(appointmentId: string): boolean {
    return this.uploadingAppointments.has(appointmentId);
  }

  uploadActionTooltip(appointment: PatientAppointment): string {
    if (this.creditsExhausted) {
      return 'Crediti analisi appuntamento esauriti';
    }
    if (
      this.isUploading(appointment.id) &&
      this.uploadProgress[appointment.id] != null
    ) {
      return 'File in caricamento';
    }
    if (
      this.isUploading(appointment.id) &&
      this.uploadProgress[appointment.id] == null
    ) {
      return 'Errore nel caricare il file. Riprova';
    }
    return 'Carica file audio';
  }

  hasBeenUploadedInThisSession(appointmentId: string): boolean {
    return this.uploadedAppointmentsInThisSession.has(appointmentId);
  }

  private updateAppointmentInDataSource(
    updatedAppointment: PatientAppointment
  ): void {
    const data = [...this.pastAppointmentsDataSource.data];
    const index = data.findIndex(
      (appointment) => appointment.id === updatedAppointment.id
    );
    if (index !== -1) {
      data[index] = { ...data[index], ...updatedAppointment };
      this.pastAppointmentsDataSource.data = data;
      this.changeDetectorRef.detectChanges();
    }
  }

  formatDate(dateString: string): string {
    const [day, month, year] = dateString.split('/');
    const date = new Date(+year, +month - 1, +day); // Month is 0-indexed in JavaScript Date
    return this.datePipe.transform(date, 'dd/MM/yyyy') || '';
  }

  onSortChange(sort: Sort) {
    const data = this.pastAppointmentsDataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.pastAppointmentsDataSource.data = data;
      return;
    }

    this.pastAppointmentsDataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'date':
          return compare(new Date(a.date), new Date(b.date), isAsc);
        case 'title':
          return compare(a.title, b.title, isAsc);
        default:
          return 0;
      }
    });
  }

  getIconClass(state: string): string {
    switch (state) {
      case 'NOT_EXECUTED':
        return 'icon-grey';
      case 'IN_PROGRESS':
        return 'icon-yellow pulsing';
      case 'EXECUTED':
        return 'icon-green';
      default:
        return 'icon-grey';
    }
  }

  private startPolling() {
    console.log('Starting polling');
    this.stopPolling(); // Ensure any existing subscription is cleared
    this.pipelineWasInProgress = false;

    this.pollingSubscription = interval(3000)
      .pipe(
        startWith(0),
        takeUntil(this.destroy$),
        switchMap(() =>
          this.patientService.getPastAppointments(this.patient.id)
        ),
        // Continue polling while services are in progress
        takeWhile(
          (appointments) =>
            appointments.some((appt) => this.hasServicesInProgress(appt)),
          true // Include the last value
        ),
        // Ensure we get one final update after all services complete
        tap((appointments) => {
          const allServicesComplete = !appointments.some((appt) =>
            this.hasServicesInProgress(appt)
          );
          if (allServicesComplete) {
            // Fetch one final time to ensure we have the latest state
            this.patientAiAnalysisService.notifyAppointmentAIOutputsCreated();
            this.performFinalUpdate();
          }
        })
      )
      .subscribe({
        next: (appointments) => {
          if (appointments.some((appt) => this.hasServicesInProgress(appt))) {
            this.pipelineWasInProgress = true;
          }
          this.updateAppointmentsData(appointments);
        },
        error: (error) => {
          console.error('Error polling appointments:', error);
          this.stopPolling();
        },
        complete: () => {
          console.log('Polling completed');
          if (!this.destroyed && this.pipelineWasInProgress) {
            this.pipelineWasInProgress = false;
            this.refreshCredits$.next();
          }
        },
      });
  }

  private performFinalUpdate(): void {
    // One final update after polling stops
    // TODO: I don't think this method is necessary
    this.patientService
      .getPastAppointments(this.patient.id)
      .pipe(take(1))
      .subscribe({
        next: (appointments) => {
          this.updateAppointmentsData(appointments);
          this.stopPolling();
        },
        error: (error) => {
          console.error('Error performing final update:', error);
          this.stopPolling();
        },
      });
  }

  private updateAppointmentsData(appointments: PatientAppointment[]): void {
    this.patientAppointments = appointments;
    this.pastAppointmentsDataSource.data = appointments;
    this.changeDetectorRef.detectChanges();
  }

  private stopPolling() {
    if (this.pollingSubscription) {
      console.log('Stopping existing polling');

      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  private setUploadProgress(appointmentId: string, progress: number): void {
    this.uploadProgress[appointmentId] = progress;
  }

  private setUploadError(appointmentId: string): void {
    delete this.uploadProgress[appointmentId];
    this.changeDetectorRef.detectChanges();
    this.errorPanelService.showErrorSnackBar(
      'Errore nel caricamento del file. Per favore riprovare.'
    );
  }

  navigateToAppointment(appointment: PatientAppointment) {
    this.router.navigate(
      ['/pazienti', this.patient.id, 'appointments', appointment.id, 'ai'],
      {
        state: {
          appointmentSelected: appointment,
        },
      }
    );
  }

  toggleInfoPanel() {
    this.showInfoPanel = !this.showInfoPanel;
  }

  navigateTo(section: string, appointment?: PatientAppointment) {
    if (appointment) {
      switch (section) {
        case 'summary':
          if (appointment.appsumm_state === 'EXECUTED') {
            this.router.navigate([
              '/pazienti',
              this.patient.id,
              'appointments',
              appointment?.id,
              'ai',
              section,
            ]);
          }
          break;
        case 'nuclear-topic':
          if (appointment.catdistr_state === 'EXECUTED') {
            this.router.navigate([
              '/pazienti',
              this.patient.id,
              'appointments',
              appointment?.id,
              'ai',
              section,
            ]);
          }
          break;
        case 'aitopic':
          if (appointment.apptopics_state === 'EXECUTED') {
            this.router.navigate([
              '/pazienti',
              this.patient.id,
              'appointments',
              appointment?.id,
              'ai',
              section,
            ]);
          }
          break;
        case 'wordcloud':
          if (appointment.appwordcloud_state === 'EXECUTED') {
            this.router.navigate([
              '/pazienti',
              this.patient.id,
              'appointments',
              appointment?.id,
              'ai',
              section,
            ]);
          }
          break;
      }
    }
  }

  openSettings() {
    const dialogRef = this.dialog.open(SettingsDialogComponent, {
      width: '400px',
      data: {
        patientId: this.patient.id,
        numSpeakers: this.patient.num_speakers || 2,
        audioLanguage: this.patient.audio_language || 'it',
        patient: this.patient,
      },
    });

    dialogRef.afterClosed().subscribe((success) => {
      if (success) {
        // No need to refresh data since we updated it directly
        // this.showPatientInterviews();
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isMobile = window.innerWidth < 1024;
  }

  private hasServicesInProgress(appointment: PatientAppointment): boolean {
    return this.getAppointmentAIPipelineState(appointment) === 'IN_PROGRESS';
  }

  areAllServicesComplete(appointment: PatientAppointment): boolean {
    return [
      appointment.transcription_state,
      appointment.appsumm_state,
      appointment.appwordcloud_state,
      appointment.catdistr_state,
      appointment.apptopics_state,
    ].every((state) => state === 'EXECUTED' || state === 'FAILED');
  }

  getAppointmentAIPipelineState(
    appointment: PatientAppointment
  ): 'NOT_READY_TO_START' | 'IN_PROGRESS' | 'SUCCESSFULLY_EXECUTED' | 'FAILED' {
    return appointment.ai_pipeline_state as
      | 'NOT_READY_TO_START'
      | 'IN_PROGRESS'
      | 'SUCCESSFULLY_EXECUTED'
      | 'FAILED';
  }

  private get isAdmin(): boolean {
    return sessionStorage.getItem('role') === 'admin';
  }

  private observeAppointmentCredits(): void {
    this.refreshCredits$
      .pipe(
        switchMap(() =>
          this.doctorService.getUsage().pipe(
            catchError((error) => {
              console.error(
                'Errore nel recuperare i crediti appuntamento',
                error
              );
              return EMPTY;
            })
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((usage) => {
        this.appointmentCredits = usage.appointment_pipeline_credits;
        if (this.creditsExhausted) {
          this.loadSubscriptionRenewal();
        } else {
          this.subscriptionRequestId++;
          this.subscriptionRenewalDate = null;
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  private loadSubscriptionRenewal(): void {
    const requestId = ++this.subscriptionRequestId;
    this.paymentsService
      .getSubscriptionData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (subscription) => {
          if (
            requestId !== this.subscriptionRequestId ||
            !this.creditsExhausted
          ) {
            return;
          }
          const willRenew =
            subscription.status === ClineaseSubscriptionStatus.ACTIVE &&
            !subscription.cancel_at_period_end;
          this.subscriptionRenewalDate = willRenew
            ? subscription.current_period_end
            : null;
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          if (requestId !== this.subscriptionRequestId) {
            return;
          }
          console.error('Errore nel recuperare i dati di abbonamento', error);
          this.subscriptionRenewalDate = null;
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  isPipelineAwaitingServices(appointment: PatientAppointment): boolean {
    if (this.getAppointmentAIPipelineState(appointment) !== 'IN_PROGRESS') {
      return false;
    }
    return [
      appointment.transcription_state,
      appointment.appsumm_state,
      appointment.appwordcloud_state,
      appointment.catdistr_state,
      appointment.apptopics_state,
    ].every((state) => !state || state === 'NOT_EXECUTED');
  }
}

function compare(
  a: number | string | Date,
  b: number | string | Date,
  isAsc: boolean
) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
